import React, { useEffect, useState } from "react";
import {
  Typography,
  Paper,
  Box,
  Grid,
  CircularProgress,
} from "@mui/material";
import { MdErrorOutline, MdLockOutline } from "react-icons/md";
import MyProgramNav from "./MyProgramNav";
import Select from "react-select";
import Swal from "sweetalert2";
import posthog from "posthog-js";
import axios from "axios";
import { generateApiKey } from "../../../helpers/common.helpers";
import {
  sendUpgradeRequest,
  fetchBulkProgramStatusService,
} from "../../../services/programs.service";
import { useQuery } from "@tanstack/react-query";
import Layout from "../../NewDashboard/Layout";
import {
  Clock,
  Mail,
  Phone,
  X,
  Sparkles,
  AlertCircle,
} from "lucide-react";

const getBaseProgramName = (name) => {
  if (!name) return "";
  const parts = name.split(" - ");
  return parts[parts.length - 1].trim().toLowerCase();
};

const calculateProgressFromStorage = () => {
  // 1. Direct progress key in localStorage
  const directKeys = [
    "curriculum_percent",
    "curriculum_progress",
    "progress_percentage",
    "course_progress",
    "progress",
  ];
  for (const key of directKeys) {
    const val = localStorage.getItem(key);
    if (val !== null && val !== undefined && val !== "") {
      const num = parseFloat(val);
      if (!isNaN(num)) return num;
    }
  }

  // 2. Derive from courseDetails or completion in localStorage
  try {
    const isComp = localStorage.getItem("is_completed");
    if (isComp === "1" || isComp === "true" || isComp === true || isComp === 1) {
      return 100;
    }
    const stored = localStorage.getItem("courseDetails");
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.terms && Array.isArray(parsed.terms) && parsed.terms.length > 0) {
        const totalTerms = parsed.terms.length;
        const activeTerm = Number(parsed.active_term ?? localStorage.getItem("term") ?? 1);
        const completedTerms = Math.max(0, activeTerm - 1);
        return Math.round((completedTerms / totalTerms) * 100);
      }
    }
  } catch (e) {
    console.error("Error reading courseDetails from localStorage:", e);
  }

  return null;
};

const UpgradeProgram = () => {
  const currentProgramName = localStorage.getItem("program_name");
  const [selectedVertical, setSelectedVertical] = useState({ label: "All verticals", value: "all" });
  const [upgradingId, setUpgradingId] = useState(null);
  const [curriculumProgress, setCurriculumProgress] = useState(() => calculateProgressFromStorage());
  const [confirmModalProgram, setConfirmModalProgram] = useState(null);
  const [successModalProgram, setSuccessModalProgram] = useState(null);

  // Fetch course details if not already in localStorage
  useEffect(() => {
    const enrol_id = localStorage.getItem("enrol_id");
    if (!localStorage.getItem("courseDetails") && enrol_id) {
      const courseData = { enrol_id };
      const options = {
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
          "page-index": generateApiKey(),
        },
      };

      axios
        .post(
          `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/dashboardhomepage/fetchcoursedetails`,
          JSON.stringify(courseData),
          options
        )
        .then((res) => {
          if (res?.data) {
            localStorage.setItem("courseDetails", JSON.stringify(res.data));
            const calculated = calculateProgressFromStorage();
            if (calculated !== null) {
              setCurriculumProgress(calculated);
            }
          }
        })
        .catch((err) => {
          console.error("Error fetching course details:", err);
        });
    }
  }, []);

  // Determine user progress tier based on curriculum progress
  // - null: not present (defaults to upgrade)
  // - < 40: locked (unlocks at 40% progress)
  // - 40 to 75: program upgrade
  // - > 75: plan your next move
  const progressTier = React.useMemo(() => {
    if (curriculumProgress === null || curriculumProgress === undefined) {
      return "upgrade";
    }
    if (curriculumProgress < 40) {
      return "locked";
    }
    if (curriculumProgress <= 75) {
      return "upgrade";
    }
    return "plan_next_move";
  }, [curriculumProgress]);

  // PostHog: capture upgrade page view on mount
  useEffect(() => {
    posthog.capture("upgrade_page_viewed", {
      source: "manage_my_program",
      current_program: currentProgramName,
      curriculum_progress: curriculumProgress !== null ? Math.round(curriculumProgress) : null,
      progress_tier: progressTier,
    });
  }, [currentProgramName, curriculumProgress, progressTier]);

  // Fetch all programs from accredian's programs JSON (cache-busting to ensure latest statuses)
  const { data: allPrograms, isLoading: isProgramsLoading, error } = useQuery({
    queryKey: ["accredian-programs"],
    queryFn: async () => {
      const res = await fetch("https://accredian.com/programs.json");
      if (!res.ok) throw new Error("Failed to fetch programs list");
      return res.json();
    },
    staleTime: 60 * 1000,
    gcTime: 0,
  });

  // Fetch dynamic program statuses from database via backend
  const { data: dbStatusMap, isLoading: isDbStatusLoading } = useQuery({
    queryKey: ["programs-db-status"],
    queryFn: async () => {
      try {
        const res = await fetchBulkProgramStatusService();
        return res?.data?.byPid || {};
      } catch (err) {
        console.error("Failed to fetch program statuses from database:", err);
        return {};
      }
    },
    staleTime: 60 * 1000,
    gcTime: 0,
  });

  const isLoading = isProgramsLoading || isDbStatusLoading;

  // Check if a program is active and eligible for upgrade
  const isProgramActive = React.useCallback(
    (program) => {
      if (!program) return false;

      // 1. Query MySQL database programs table status by pid
      // If status is inactive, or registration_status is inactive, or isActive is false, program is closed
      if (program.pid && dbStatusMap && dbStatusMap[program.pid]) {
        const dbInfo = dbStatusMap[program.pid];
        if (
          dbInfo.status !== "active" ||
          dbInfo.registration_status === "inactive" ||
          dbInfo.isActive === false
        ) {
          return false;
        }
      }

      // 2. Filter out programs with closed status (catches 'admission closed', 'admissions closed', 'closed', 'close', etc.)
      const status = (program.status || "").trim().toLowerCase();
      if (
        !status ||
        status.includes("close") ||
        status.includes("closed") ||
        status === "admission closed" ||
        status === "admissions closed" ||
        status === "inactive" ||
        status === "archived"
      ) {
        return false;
      }

      // 3. Filter out excluded categories
      const category = (program.category || "").trim().toLowerCase();
      const excludedCategories = ["microsoft", "free courses"];
      if (excludedCategories.includes(category)) {
        return false;
      }

      // 4. Filter out the currently enrolled program
      if (program.program_name && currentProgramName) {
        const enrolledBase = getBaseProgramName(currentProgramName);
        const programBase = getBaseProgramName(program.program_name);
        if (enrolledBase && programBase && enrolledBase === programBase) {
          return false;
        }
      }

      return true;
    },
    [currentProgramName, dbStatusMap]
  );

  // Get all active, upgradeable programs
  const activePrograms = React.useMemo(() => {
    if (!allPrograms || !Array.isArray(allPrograms)) return [];
    return allPrograms.filter(isProgramActive);
  }, [allPrograms, isProgramActive]);

  // Extract unique categories for dropdown options from active programs list
  const categoryOptions = React.useMemo(() => {
    if (!activePrograms || activePrograms.length === 0) {
      return [{ label: "All verticals", value: "all" }];
    }

    const uniqueCategories = new Set();
    activePrograms.forEach((p) => {
      if (p.category) {
        uniqueCategories.add(p.category.trim().toLowerCase());
      }
    });

    const options = Array.from(uniqueCategories).map((cat) => {
      const formattedLabel = cat
        .split(" ")
        .map((word) => {
          if (word.toLowerCase() === "cxo") return "CXO";
          return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(" ");

      return {
        label: formattedLabel,
        value: cat,
      };
    });

    return [{ label: "All verticals", value: "all" }, ...options];
  }, [activePrograms]);

  // Filter programs based on selected category (vertical)
  const filteredPrograms = React.useMemo(() => {
    if (selectedVertical && selectedVertical.value !== "all") {
      return activePrograms.filter(
        (program) =>
          program.category &&
          program.category.trim().toLowerCase() === selectedVertical.value.toLowerCase()
      );
    }
    return activePrograms;
  }, [activePrograms, selectedVertical]);

  // Price mapper formatting program_fee, price, or falling back to N/A
  const getPrice = (program) => {
    if (program.program_fee) {
      const fee = Number(program.program_fee);
      if (!isNaN(fee)) {
        return `Starting at ₹${fee.toLocaleString("en-IN")}`;
      }
    }
    if (program.price) return program.price;

    return "N/A";
  };

  // Safe Start Date parser/formatter
  const getNextCohort = (program) => {
    if (program.next_cohort) return program.next_cohort;
    if (program.start_date) return program.start_date;
    return "Coming Soon";
  };

  // Track "View Program" click
  const handleViewProgramClick = (program) => {
    try {
      const payload = {
        program_name: program.program_name,
        university_name: program.university_name,
        url: program.url,
        current_program: currentProgramName,
        curriculum_progress: curriculumProgress !== null ? Math.round(curriculumProgress) : null,
        progress_tier: progressTier,
      };
      posthog.capture("view_program_clicked", payload);
      console.log("[PostHog] view_program_clicked captured:", payload);
    } catch (e) {
      console.error("[PostHog] Error capturing view_program_clicked:", e);
    }
  };

  // Open confirmation modal
  const handleRequestUpgrade = (program) => {
    if (progressTier === "locked") {
      Swal.fire({
        title: "Feature Locked",
        text: "Program Upgrade unlocks at 40% course progress.",
        icon: "info",
        confirmButtonColor: "#1A73E8",
      });
      return;
    }
    setConfirmModalProgram(program);
  };

  // Submit upgrade request to backend after user confirms
  const handleConfirmSubmit = async () => {
    if (!confirmModalProgram) return;
    const program = confirmModalProgram;
    const isPlanNextMove = progressTier === "plan_next_move";

    const sendDataresult = {
      user_id: localStorage.getItem("user_id"),
      enrol_id: localStorage.getItem("enrol_id"),
      current_pid: localStorage.getItem("program_id"),
      new_pid: program.pid,
      vertical: program.category,
      program: program.program_name,
      email: localStorage.getItem("email"),
      firstname: localStorage.getItem("username"),
      lastname: localStorage.getItem("lastname"),
      mobile: localStorage.getItem("mobile"),
      batch: localStorage.getItem("batch"),
      current_program: currentProgramName,
      curriculum_progress:
        curriculumProgress !== null ? Math.round(curriculumProgress) : null,
      request_type: isPlanNextMove ? "plan_next_move" : "program_upgrade",
    };

    setUpgradingId(program.id);
    try {
      const response = await sendUpgradeRequest(sendDataresult);
      if (response) {
        posthog.capture("upgrade_request_submitted", {
          current_program: currentProgramName,
          choosen_program: program.program_name,
          curriculum_progress:
            curriculumProgress !== null ? Math.round(curriculumProgress) : null,
          progress_tier: progressTier,
        });

        // Close confirmation modal & open beautiful success modal
        setConfirmModalProgram(null);
        setSuccessModalProgram(program);
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: "Submission Failed",
        text:
          error.message ||
          "An error occurred while sending the upgrade request. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#1A73E8",
      });
    } finally {
      setUpgradingId(null);
    }
  };

  // Custom styles for react-select dropdown
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      border: "2px solid #e2e8f0",
      borderRadius: "8px",
      padding: "4px 8px",
      outline: "none",
      fontSize: "15px",
      fontWeight: 500,
      fontFamily: "Inter, sans-serif",
      textTransform: "capitalize",
      boxShadow: state.isFocused ? "0 0 0 2px #3b82f6" : "none",
      borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
      "&:hover": {
        borderColor: state.isFocused ? "#3b82f6" : "#cbd5e1",
      },
      backgroundColor: "#ffffff",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#94a3b8",
      fontFamily: "Inter, sans-serif",
    }),
    option: (provided, state) => ({
      ...provided,
      textTransform: "capitalize",
      fontFamily: "Inter, sans-serif",
      fontSize: "15px",
      backgroundColor: state.isSelected ? "#3b82f6" : state.isFocused ? "#f1f5f9" : "#ffffff",
      color: state.isSelected ? "#ffffff" : "#334155",
      "&:active": {
        backgroundColor: "#e2e8f0",
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#334155",
    }),
  };

  // Dynamic titles and subtitles based on tier
  const pageTitle = progressTier === "plan_next_move" ? "Plan Your Next Move" : "Upgrade Program";
  const pageSubtitle =
    progressTier === "plan_next_move"
      ? "Explore advanced programs that align with your next career move, or browse popular programs below."
      : "Select a vertical to explore available programs, or browse popular programs below.";

  const gridHeaderTitle =
    progressTier === "plan_next_move"
      ? "Popular programs to plan your next move"
      : "Popular programs among learners";

  return (
    <Layout>
      <Box sx={{ backgroundColor: "#f3f6f9", minHeight: "100vh", pb: 5 }}>
        <Box sx={{ pt: { lg: 8, xs: 3 }, px: { xs: 2, lg: 0 } }}>
          {/* Sub Navigation */}
          {/* <Box>
            <MyProgramNav />
          </Box> */}

          {/* Heading Text */}
          <Box sx={{ ml: { lg: 3 }, mr: { lg: 10 }, mb: 3, mt: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, mb: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: "1.75rem", md: "2.25rem" },
                  color: "#1e293b",
                  fontFamily: "Outfit, Inter, sans-serif",
                }}
              >
                {pageTitle}
              </Typography>

              {/* {progressTier === "locked" && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                  <MdLockOutline size={14} /> Unlocks at 40% Progress
                </span>
              )} */}

              {/* {progressTier === "upgrade" && curriculumProgress !== null && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                  Progress: {Math.round(curriculumProgress)}%
                </span>
              )} */}

              {/* {progressTier === "plan_next_move" && curriculumProgress !== null && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  Progress: {Math.round(curriculumProgress)}%
                </span>
              )} */}
            </Box>

            <Typography
              variant="body1"
              sx={{
                color: "#64748b",
                fontSize: { xs: "0.9rem", md: "1rem" },
                fontFamily: "Inter, sans-serif",
              }}
            >
              {pageSubtitle}
            </Typography>
          </Box>

          {/* Lock Banner if Progress < 39.9 */}
          {progressTier === "locked" && (
            <Box sx={{ ml: { lg: 3 }, mr: { lg: 10 }, mb: 4 }}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  backgroundColor: "#fffbeb",
                  border: "1px solid #fde68a",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "10px",
                    backgroundColor: "#fef3c7",
                    color: "#d97706",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <MdLockOutline size={22} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      color: "#92400e",
                      fontFamily: "Outfit, Inter, sans-serif",
                    }}
                  >
                    Program Upgrade is Locked
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      color: "#b45309",
                      fontFamily: "Inter, sans-serif",
                      mt: 0.25,
                    }}
                  >
                    This feature unlocks at <strong>40% Progress</strong>
                    {curriculumProgress !== null
                      ? ` (your current progress is ${Math.round(curriculumProgress)}%)`
                      : ""}
                    . You can explore available programs below in the meantime.
                  </Typography>
                </Box>
              </Paper>
            </Box>
          )}

          {/* Choose Vertical Panel */}
          <Box sx={{ ml: { lg: 3 }, mr: { lg: 10 }, mb: 5 }}>
            <Paper
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: "12px",
                boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.05), 0px 1px 2px rgba(0, 0, 0, 0.05)",
                border: "1px solid #e2e8f0",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  letterSpacing: "0.05em",
                  color: "#64748b",
                  textTransform: "uppercase",
                  mb: 1.5,
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Choose Vertical
              </Typography>
              <Box sx={{ maxWidth: { md: "400px" } }}>
                <Select
                  options={categoryOptions}
                  value={selectedVertical}
                  onChange={(option) =>
                    setSelectedVertical(option || { label: "All verticals", value: "all" })
                  }
                  styles={customStyles}
                  isSearchable={false}
                  placeholder="Choose vertical"
                />
              </Box>
            </Paper>
          </Box>

          {/* Programs Section */}
          <Box sx={{ ml: { lg: 3 }, mr: { lg: 10 } }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                  color: "#1e293b",
                  mb: 0.5,
                  fontFamily: "Outfit, Inter, sans-serif",
                }}
              >
                {gridHeaderTitle}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#64748b",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Explore programs that align with different career paths
              </Typography>
            </Box>

            {/* Program Listing Grid */}
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 8,
                  gap: 2,
                }}
              >
                <CircularProgress size={28} />
                <Typography sx={{ color: "#64748b", fontWeight: 500, fontFamily: "Inter, sans-serif" }}>
                  Loading programs...
                </Typography>
              </Box>
            ) : error ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <Typography sx={{ color: "#ef4444", fontWeight: 500, fontFamily: "Inter, sans-serif" }}>
                  Error loading programs. Please try again later.
                </Typography>
              </Box>
            ) : filteredPrograms.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 8,
                  px: 3,
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  border: "1px dashed #cbd5e1",
                }}
              >
                <MdErrorOutline size={36} color="#94a3b8" />
                <Typography
                  sx={{
                    color: "#64748b",
                    mt: 2,
                    fontWeight: 500,
                    textAlign: "center",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  No programs available for upgrade in this vertical.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {filteredPrograms.map((program) => (
                  <Grid item xs={12} sm={6} lg={4} key={program.id || program.program_name}>
                    <Paper
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        borderRadius: "12px",
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.02), 0 1px 2px rgba(0,0,0,0.03)",
                        overflow: "hidden",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow:
                            "0 12px 20px -8px rgba(0,0,0,0.08), 0 4px 12px -5px rgba(0,0,0,0.03)",
                          borderColor: "#3b82f6",
                        },
                      }}
                    >
                      {/* upper part */}
                      <Box sx={{ p: 3, flexGrow: 1 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "1.05rem",
                              lineHeight: 1.4,
                              color: "#0f172a",
                              mb: 0.5,
                              fontFamily: "Outfit, Inter, sans-serif",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              minHeight: "2.9rem",
                            }}
                          >
                            {program.program_name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.85rem",
                              fontWeight: 500,
                              color: "#64748b",
                              fontFamily: "Inter, sans-serif",
                            }}
                          >
                            {program.university_name}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 3 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                color: "#64748b",
                                fontFamily: "Inter, sans-serif",
                              }}
                            >
                              Price
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                fontWeight: 700,
                                color: "#1e293b",
                                fontFamily: "Inter, sans-serif",
                              }}
                            >
                              {getPrice(program)}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                color: "#64748b",
                                fontFamily: "Inter, sans-serif",
                              }}
                            >
                              Duration
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                fontWeight: 700,
                                color: "#1e293b",
                                fontFamily: "Inter, sans-serif",
                              }}
                            >
                              {program.duration || "N/A"}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                color: "#64748b",
                                fontFamily: "Inter, sans-serif",
                              }}
                            >
                              Next cohort
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                fontWeight: 700,
                                color: "#1e293b",
                                fontFamily: "Inter, sans-serif",
                              }}
                            >
                              {getNextCohort(program)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* lower part */}
                      <Box
                        sx={{
                          px: 3,
                          pb: 3,
                          pt: 1,
                          display: "flex",
                          gap: 2,
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        {program.url ? (
                          <a
                            href={program.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => handleViewProgramClick(program)}
                            className="text-sm font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors py-2 whitespace-nowrap"
                          >
                            View Program <span style={{ fontSize: "1.1rem", lineHeight: 1 }}>↗</span>
                          </a>
                        ) : (
                          <div />
                        )}

                        {progressTier === "locked" ? (
                          <button
                            disabled={true}
                            className="py-2.5 px-3 text-center text-xs sm:text-sm font-semibold rounded-lg bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed flex items-center justify-center gap-1.5 transition-all"
                            title="Unlocks at 40% course progress"
                          >
                            <MdLockOutline size={15} /> Unlocks at 40% Progress
                          </button>
                        ) : progressTier === "plan_next_move" ? (
                          <button
                            onClick={() => handleRequestUpgrade(program)}
                            disabled={upgradingId !== null}
                            className={`py-2.5 px-4 text-center text-sm font-semibold rounded-lg transition-all text-white shadow-sm hover:shadow-md ${upgradingId === program.id
                              ? "bg-emerald-400 cursor-not-allowed"
                              : "bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 cursor-pointer"
                              }`}
                          >
                            {upgradingId === program.id ? "Requesting..." : "Plan Your Next Move"}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleRequestUpgrade(program)}
                            disabled={upgradingId !== null}
                            className={`py-2.5 px-5 text-center text-sm font-semibold rounded-lg transition-all text-white shadow-sm hover:shadow-md ${upgradingId === program.id
                              ? "bg-blue-400 cursor-not-allowed"
                              : "bg-blue-600 hover:bg-blue-700 active:bg-blue-800 cursor-pointer"
                              }`}
                          >
                            {upgradingId === program.id ? "Requesting..." : "Request Upgrade"}
                          </button>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Box>
      </Box>

      {/* =========================================================================
          CONFIRMATION MODAL: Cancel on the Left, Request Upgrade on the Right
         ========================================================================= */}
      {confirmModalProgram && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setConfirmModalProgram(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-100 flex flex-col animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top Header */}
            <div className="bg-slate-50/90 px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div
                  className={`w-9 h-9 rounded-xl flex items-center justify-center ${progressTier === "plan_next_move"
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-blue-100 text-blue-700"
                    }`}
                >
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg leading-tight">
                    {progressTier === "plan_next_move"
                      ? "Plan Your Next Move"
                      : "Confirm Program Upgrade"}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {progressTier === "plan_next_move"
                      ? "Request enrollment details for this advanced program"
                      : "Submit your request to upgrade to this program"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setConfirmModalProgram(null)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200/50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* Program Preview Card */}
              <div className="bg-gradient-to-br from-slate-50 to-blue-50/40 rounded-xl p-4 border border-blue-100/70 space-y-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] uppercase font-bold tracking-wider text-blue-700 bg-blue-100/80 px-2.5 py-0.5 rounded-full">
                    {confirmModalProgram.university_name ||
                      confirmModalProgram.category ||
                      "Accredian"}
                  </span>
                  {confirmModalProgram.duration && (
                    <span className="text-xs text-gray-500 flex items-center gap-1 font-medium">
                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                      {confirmModalProgram.duration}
                    </span>
                  )}
                </div>

                <h4 className="font-bold text-gray-900 text-base md:text-lg leading-snug">
                  {confirmModalProgram.program_name}
                </h4>

                <div className="text-xs text-gray-500 pt-2 border-t border-blue-100/60 flex items-center justify-between">
                  <span>
                    Next Cohort:{" "}
                    <strong className="text-gray-800">
                      {getNextCohort(confirmModalProgram)}
                    </strong>
                  </span>
                  {confirmModalProgram.category && (
                    <span className="capitalize text-gray-500">
                      Vertical: {confirmModalProgram.category}
                    </span>
                  )}
                </div>
              </div>

              {/* Current Program Row */}
              {currentProgramName && (
                <div className="flex items-center justify-between text-xs px-3.5 py-2.5 bg-gray-50/80 rounded-xl text-gray-600 border border-gray-100">
                  <span className="text-gray-500">Currently Enrolled:</span>
                  <span className="font-semibold text-gray-800 text-right truncate max-w-[240px]">
                    {currentProgramName}
                  </span>
                </div>
              )}

              {/* Advisory note */}
              <div className="bg-amber-50/80 border border-amber-200/70 rounded-xl p-3 flex items-start gap-2.5 text-xs text-amber-900">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="leading-relaxed">
                  Our academics team will review your request and reach out to you within 72 hours.                </p>
              </div>
            </div>

            {/* Footer Buttons: Cancel on the LEFT, Request Upgrade on the RIGHT */}
            <div className="px-6 py-4 bg-gray-50/80 border-t border-gray-100 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setConfirmModalProgram(null)}
                className="px-5 py-2.5 rounded-xl border border-gray-300 hover:bg-gray-100 text-gray-700 font-semibold text-sm transition-all focus:outline-none"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmSubmit}
                disabled={upgradingId !== null}
                className={`px-6 py-2.5 rounded-xl text-white font-semibold text-sm shadow-md transition-all flex items-center gap-2 cursor-pointer ${upgradingId !== null
                  ? "bg-gray-400 cursor-not-allowed"
                  : progressTier === "plan_next_move"
                    ? "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 active:scale-[0.98] shadow-emerald-500/20"
                    : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 active:scale-[0.98] shadow-blue-500/20"
                  }`}
              >
                {upgradingId !== null ? (
                  <>
                    <CircularProgress size={16} sx={{ color: "#ffffff" }} />
                    <span>Submitting...</span>
                  </>
                ) : progressTier === "plan_next_move" ? (
                  "Yes, Plan My Next Move"
                ) : (
                  "Yes, Request Upgrade"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          SUCCESS MODAL: Exactly matching the design in user's uploaded screenshot
         ========================================================================= */}
      {successModalProgram && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setSuccessModalProgram(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden border border-gray-100 flex flex-col animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top Header Bar (Subtle lavender background, matching screenshot) */}
            <div className="bg-[#f8f7ff] border-b border-[#ece8ff] px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-900 text-base md:text-lg leading-tight">
                  {progressTier === "plan_next_move"
                    ? "Next Move Submitted!"
                    : "Upgrade Submitted!"}
                </h3>
                <p className="text-xs md:text-sm text-gray-500 mt-0.5">
                  We'll review your request and get back to you soon
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSuccessModalProgram(null)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200/50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 md:p-8 space-y-6 max-h-[85vh] overflow-y-auto">
              {/* Large Green Checkmark Circle Icon */}
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#dcfce7] border border-[#bbf7d0] flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-[#16a34a]"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h2 className="text-2xl md:text-[1.65rem] font-bold text-gray-900 leading-tight">
                  {progressTier === "plan_next_move"
                    ? "Plan Your Next Move Submitted! 🎉"
                    : "Upgrade Request Submitted! 🎉"}
                </h2>
                <p className="text-gray-600 text-sm md:text-[0.95rem] mt-2.5 max-w-md mx-auto leading-relaxed">
                  Your request to{" "}
                  {progressTier === "plan_next_move" ? "explore" : "upgrade to"}{" "}
                  <strong className="font-bold text-gray-900">
                    {successModalProgram.program_name}
                  </strong>{" "}
                  has been successfully submitted.
                </p>
              </div>

              {/* Card 1: What happens next? (Light blue card, identical to screenshot) */}
              <div className="bg-[#f0f7ff]/90 border border-[#dbeafe] rounded-2xl p-5 space-y-4 text-left">
                <div className="flex items-center gap-2 text-gray-900 font-bold text-sm md:text-base">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <span>What happens next?</span>
                </div>

                <div className="space-y-3.5">
                  {/* Step 1 */}
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                      1
                    </div>
                    <div>
                      <h5 className="font-bold text-gray-900 text-sm leading-tight">
                        Review Process
                      </h5>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Our team will review your request within 72 hours
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                      2
                    </div>
                    <div>
                      <h5 className="font-bold text-gray-900 text-sm leading-tight">
                        Personal Consultation
                      </h5>
                      <p className="text-xs text-gray-500 mt-0.5">
                        We'll schedule a call to discuss your goals and next steps
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                      3
                    </div>
                    <div>
                      <h5 className="font-bold text-gray-900 text-sm leading-tight">
                        Program Transition
                      </h5>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Seamless transition to your new program with full support
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 2: Need immediate assistance? (Light purple card, identical to screenshot) */}
              <div className="bg-[#fbf8ff] border border-[#f0e8ff] rounded-2xl p-4 text-center">
                <h5 className="text-xs md:text-sm font-semibold text-gray-800 mb-3">
                  Need immediate assistance?
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
                  {/* Email Support */}
                  <div className="bg-white rounded-xl p-3 border border-purple-100/70 shadow-sm flex items-center gap-3 hover:border-purple-300 transition-colors">
                    <div className="w-9 h-9 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="text-[11px] font-bold text-gray-800 block">
                        Email Support
                      </span>
                      <a
                        href="mailto:admissions@accredian.com"
                        className="text-xs text-purple-600 hover:text-purple-700 font-medium hover:underline block truncate"
                      >
                        academics@accredian.com
                      </a>
                    </div>
                  </div>

                  {/* Phone Support */}
                  <div className="bg-white rounded-xl p-3 border border-purple-100/70 shadow-sm flex items-center gap-3 hover:border-purple-300 transition-colors">
                    <div className="w-9 h-9 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="text-[11px] font-bold text-gray-800 block">
                        Phone Support
                      </span>
                      <a
                        href="tel:07969547220"
                        className="text-xs text-purple-600 hover:text-purple-700 font-medium hover:underline block truncate"
                      >
                        07969547220
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Done / Close Button */}
              <button
                type="button"
                onClick={() => setSuccessModalProgram(null)}
                className="w-full py-3 rounded-xl bg-gray-900 hover:bg-gray-800 text-white font-semibold text-sm transition-all shadow-md focus:outline-none"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default UpgradeProgram;
